import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import type { Node, XYPosition } from '@xyflow/react'
import type { NoteNodeData } from '../nodes/NoteNode.types'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import { snapToGrid, type MovingRect } from '../utils/canvasAlignment'
import { MIN_AREA_SIZE } from '../utils/canvasArea'
import type { CanvasPlacementMode } from '../ProjectCanvas.types'

/** Was der Hook zum Anlegen braucht — dieselben Stücke wie `placeNoteAt`. */
interface UseAreaDrawingOptions {
  placementMode: CanvasPlacementMode
  canvasElement: HTMLElement | null
  projectId: string
  screenToFlowPosition: (p: { x: number; y: number }) => XYPosition
  setNodes: React.Dispatch<React.SetStateAction<Node<NoteNodeData>[]>>
  recordStep: (build: (step: {
    created: (id: string, at: Date) => void
    update: (id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => void
  }) => void) => void
  adoptUnder: (
    step: { update: (id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => void },
    areaId: string,
    rect: MovingRect,
  ) => void
  onPlacementConsumed: () => void
  /** Der Vorgabename des frischen Bereichs. */
  areaTitle: () => string
  /**
   * Die Farbe, die der aufgezogene Bereich bekommt — **zuletzt gewählt gilt
   * weiter** (CONTEXT.md).
   *
   * Als Funktion wie `areaTitle`: Der Wert ändert sich, während der Hook lebt,
   * und über eine Prop bände er seine Zeigerlauscher bei jedem Farbwechsel neu
   * ab und wieder an.
   */
  areaColor: () => string
  /** Rasterweite und Klick-Schwelle — beide gehören dem Canvas, nicht der Geste. */
  grid: number
  clickThreshold: number
  /**
   * Meldet die Kennung des frischen Bereichs an, damit die Auswahl auf ihn
   * wandert, sobald seine Zeile eintrifft — derselbe Weg wie beim Klick
   * (`placeNoteAt`) und bei ⌘D.
   */
  selectWhenArrived: (id: string) => void
}

/**
 * **Aufziehen** — die eine Geste, die es nur beim **Bereich** gibt.
 *
 * Aus `CanvasFlow` gezogen (2026-09-05, siehe `.claude/tasks/large-files.md`):
 * ein abgeschlossener Effekt mit zwei Ausgängen. `drawing` ist das Rechteck
 * für die Vorschau, `drewRef` der Merker, mit dem `handlePaneClick` den Klick
 * schluckt, den der Browser nach dem Loslassen noch meldet.
 */
export function useAreaDrawing({
  placementMode, canvasElement, projectId, screenToFlowPosition,
  setNodes, recordStep, adoptUnder, onPlacementConsumed, areaTitle, areaColor,
  grid, clickThreshold, selectWhenArrived,
}: UseAreaDrawingOptions) {
  const dataSource = useDataSource()
  /*
    **Aufziehen** — die eine Geste, die es nur beim Bereich gibt: Mit scharfem
    Bereichs-Werkzeug zieht ein Zug auf der Fläche ein Rechteck auf, das beim
    Loslassen der Bereich wird. Ein Klick ohne Zug legt weiterhin die
    Standardgröße ab (`handlePaneClick`). Solange das Werkzeug scharf ist,
    ruht das Auswahlrechteck (`selectionOnDrag` unten) — beide wollen denselben
    Zug.
  */
  const [drawing, setDrawing] = useState<MovingRect | null>(null)
  const drewRef = useRef(false)
  useEffect(() => {
    if (placementMode !== 'area' || !canvasElement) return
    let origin: XYPosition | null = null
    let originClient: { x: number; y: number } | null = null
    let rect: MovingRect | null = null
    const rectFrom = (client: { x: number; y: number }) => {
      const to = screenToFlowPosition(client)
      const x1 = snapToGrid(Math.min(origin!.x, to.x), grid)
      const y1 = snapToGrid(Math.min(origin!.y, to.y), grid)
      const x2 = snapToGrid(Math.max(origin!.x, to.x), grid)
      const y2 = snapToGrid(Math.max(origin!.y, to.y), grid)
      return { x: x1, y: y1, width: Math.max(x2 - x1, MIN_AREA_SIZE), height: Math.max(y2 - y1, MIN_AREA_SIZE) }
    }
    const stop = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
    }
    const handleMove = (e: PointerEvent) => {
      if (!origin || !originClient) return
      // Bis zur Klick-Schwelle bleibt es ein Klick — der legt die Standardgröße ab.
      if (!rect && Math.hypot(e.clientX - originClient.x, e.clientY - originClient.y) <= clickThreshold) return
      rect = rectFrom({ x: e.clientX, y: e.clientY })
      setDrawing(rect)
    }
    const handleCancel = () => {
      stop()
      origin = null
      rect = null
      setDrawing(null)
    }
    const handleUp = () => {
      stop()
      const drawn = rect
      origin = null
      rect = null
      setDrawing(null)
      if (!drawn) return
      // Der Klick, den der Browser gleich noch meldet, darf keinen zweiten legen.
      drewRef.current = true
      const id = crypto.randomUUID()
      setNodes(ns => (ns.some(n => n.selected) ? ns.map(n => (n.selected ? { ...n, selected: false } : n)) : ns))
      // Ausgewählt, sobald er dasteht (siehe `placeNoteAt`).
      selectWhenArrived(id)
      recordStep(step => {
        step.created(id, new Date())
        adoptUnder(step, id, drawn)
      })
      // Mit Vorgabenamen im Reiter (siehe `placeNoteAt`).
      // Farbe wie beim Absetzen (`placeNoteAt`): Aufziehen und Ablegen dürfen
      // nicht verschiedene Bereiche liefern.
      void dataSource.createCanvasNote({ id, projectId, kind: 'area', title: areaTitle(), content: '', x: drawn.x, y: drawn.y, width: drawn.width, height: drawn.height, color: areaColor() })
      onPlacementConsumed()
    }
    const handleDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      const target = e.target as Element | null
      if (!target?.closest?.('.react-flow__pane')) return
      originClient = { x: e.clientX, y: e.clientY }
      origin = screenToFlowPosition(originClient)
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleCancel)
    }
    canvasElement.addEventListener('pointerdown', handleDown)
    return () => {
      canvasElement.removeEventListener('pointerdown', handleDown)
      stop()
      setDrawing(null)
    }
  }, [placementMode, canvasElement, screenToFlowPosition, setNodes, recordStep, adoptUnder, projectId, onPlacementConsumed, areaTitle, areaColor, grid, clickThreshold, selectWhenArrived, dataSource])

  return { drawing, drewRef }
}
